import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("sams_user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/profile");
      setUser(data);
      localStorage.setItem("sams_user", JSON.stringify(data));
    } catch {
      setUser(null);
      localStorage.removeItem("sams_token");
      localStorage.removeItem("sams_user");
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("sams_token")) refresh();
    else setChecking(false);
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/login", { email, password });
    localStorage.setItem("sams_token", data.token);
    localStorage.setItem("sams_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/logout"); } catch { /* ignore */ }
    localStorage.removeItem("sams_token");
    localStorage.removeItem("sams_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, refresh, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export const ROLE_LABEL = {
  system_admin: "System Administrator",
  sd_admin: "SD Admin",
  auditor: "Auditor",
  manager: "Manager",
  supplier: "Supplier",
};

export function hasRole(user, ...roles) {
  if (!user) return false;
  if (user.role === "system_admin") return true;
  return roles.includes(user.role);
}
